// SPDX-License-Identifier: MIT
// Pure parser for the NDJSON emitted by
//   claude -p --output-format stream-json --include-partial-messages --verbose
// (Claude Code 2.x). Turns each raw line into ONE semantic UI event, or null for
// noise. Grounded on a real capture (tests/fixtures/claude-stream-sample.ndjson),
// NOT a cheatsheet: the Anthropic streaming deltas are wrapped in
//   {"type":"stream_event","event":{...}}
// tool results come back as {"type":"user", ...tool_use_result}, and there is
// {"type":"system","subtype":"status|hook_*"} + rate_limit_event noise to drop.
// The Rust side just forwards lines over a Channel ; all UI meaning lives here so
// it is unit-testable without Tauri.
import type { Locale } from '../i18n/detect';

export type AgentStreamEvent =
  | { kind: 'init' }
  | { kind: 'thinking' }
  | { kind: 'text'; delta: string }
  | { kind: 'tool_start'; tool: string; label: string; target?: string }
  | { kind: 'tool_done'; target?: string }
  | { kind: 'done'; text: string; isError: boolean; costUsd?: number; durationMs?: number; numTurns?: number };

interface Json { [k: string]: unknown }
function obj(v: unknown): Json | null { return v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null; }
function str(v: unknown): string | undefined { return typeof v === 'string' ? v : undefined; }
function num(v: unknown): number | undefined { return typeof v === 'number' ? v : undefined; }

export function parseClaudeStreamLine(line: string): AgentStreamEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let o: Json | null;
  try { o = obj(JSON.parse(trimmed)); } catch { return null; }
  if (!o) return null;

  switch (o.type) {
    case 'system':
      // init carries cwd + tools ; everything else (status, hook_started,
      // hook_response) is plumbing noise.
      return o.subtype === 'init' ? { kind: 'init' } : null;

    case 'stream_event': {
      const ev = obj(o.event);
      if (!ev) return null;
      if (ev.type === 'content_block_start') {
        const cb = obj(ev.content_block);
        if (!cb) return null;
        if (cb.type === 'tool_use') {
          const tool = str(cb.name) ?? 'outil';
          const { label, target } = friendlyToolLabel(tool, cb.input);
          return { kind: 'tool_start', tool, label, target };
        }
        if (cb.type === 'thinking') return { kind: 'thinking' };
        return null; // 'text' block start — its content arrives as deltas
      }
      if (ev.type === 'content_block_delta') {
        const d = obj(ev.delta);
        if (d && d.type === 'text_delta') {
          const t = str(d.text);
          return t ? { kind: 'text', delta: t } : null;
        }
        return null; // signature_delta / thinking_delta / input_json_delta → noise here
      }
      return null; // message_start / message_delta / message_stop / content_block_stop
    }

    case 'user': {
      // Tool results echo back as a user turn ; tool_use_result holds the structured
      // outcome (e.g. {type:"create", filePath}). Marks a tool as completed.
      const tur = obj(o.tool_use_result);
      const target = tur ? (str(tur.filePath) ?? str(tur.file_path)) : undefined;
      const msg = obj(o.message);
      const content = msg && Array.isArray(msg.content) ? (msg.content as unknown[]) : [];
      const hasToolResult = content.some((c) => obj(c)?.type === 'tool_result');
      return hasToolResult ? { kind: 'tool_done', target } : null;
    }

    case 'result':
      return {
        kind: 'done',
        text: str(o.result) ?? '',
        isError: o.is_error === true || (o.subtype !== undefined && o.subtype !== 'success'),
        costUsd: num(o.total_cost_usd),
        durationMs: num(o.duration_ms),
        numTurns: num(o.num_turns),
      };

    default:
      return null; // assistant (redundant w/ deltas), rate_limit_event, anything new
  }
}

function basename(p: string | undefined): string | undefined {
  if (!p) return undefined;
  const parts = p.split(/[\\/]/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : undefined;
}

// Map a raw tool name + (possibly partial) input to a warm, non-technical line in
// the user's language. Never returns an empty label. `input` may be {} at
// tool_start (the args stream in afterwards) — then we fall back to a verb only.
export function friendlyToolLabel(
  tool: string,
  input?: unknown,
  locale: Locale = 'fr',
): { label: string; target?: string } {
  const fr = locale === 'fr';
  const i = obj(input) ?? {};
  const file = basename(str(i.file_path) ?? str(i.path) ?? str(i.notebook_path));
  const withTarget = (verb: string) => ({ label: file ? `${verb} ${file}` : verb, target: file });

  switch (tool) {
    case 'Write':
      return withTarget(fr ? '✍️ J’écris' : '✍️ Writing');
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit':
      return withTarget(fr ? '✍️ Je modifie' : '✍️ Editing');
    case 'Read':
      return withTarget(fr ? '👀 Je relis' : '👀 Reading');
    case 'Glob':
    case 'Grep':
      return { label: fr ? '🔍 Je cherche dans le projet' : '🔍 Searching the project' };
    case 'WebFetch':
    case 'WebSearch':
      return { label: fr ? '🌐 Je consulte le web' : '🌐 Browsing the web' };
    case 'Bash': {
      const cmd = (str(i.command) ?? '').toLowerCase();
      if (/\b(npm|pnpm|yarn|bun)\b[^&|;]*\b(i|install|ci|add)\b/.test(cmd))
        return { label: fr ? '📦 J’installe les dépendances' : '📦 Installing dependencies' };
      if (/\bgit\b/.test(cmd)) return { label: fr ? '🌿 Je sauvegarde (git)' : '🌿 Saving (git)' };
      if (/\bmkdir\b/.test(cmd)) return { label: fr ? '📁 Je prépare le dossier' : '📁 Setting up folders' };
      if (/\b(npm|pnpm|yarn|bun)\b.*\b(run|dev|build|start|test)\b/.test(cmd))
        return { label: fr ? '⚙️ Je lance le projet' : '⚙️ Running the project' };
      return { label: fr ? '⚙️ Je lance une commande' : '⚙️ Running a command' };
    }
    default:
      return { label: `🔧 ${tool}` };
  }
}
